import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * MenuDropdown — generic dropdown menu primitive.
 *
 * Powers the archetype-aware "+ New X" pattern (Design Principles §3.11)
 * and any other contextual / action menu in the platform.
 *
 * The trigger is composable — pass a `trigger` element (typically a
 * Button) and the dropdown manages open/close state, click-outside
 * dismissal, Escape dismissal, and keyboard navigation.
 *
 * Items are descriptors: `{ id, label, description?, icon?, iconTone?, disabled?, onSelect? }`.
 * Render a divider with `{ id, divider: true }`.
 *
 * @param {Object} props
 * @param {React.ReactElement}                                                  props.trigger
 *   The element that toggles the menu. Click-handler is augmented internally.
 * @param {Array<{
 *   id: string,
 *   label?: React.ReactNode,
 *   description?: React.ReactNode,
 *   icon?: React.ReactNode,
 *   iconTone?: 'action'|'brand'|'trust'|'cyan'|'success'|'warning'|'danger'|'muted',
 *   disabled?: boolean,
 *   onSelect?: () => void,
 *   divider?: boolean,
 *   header?: React.ReactNode,
 *   footer?: React.ReactNode,
 * }>} props.items
 * @param {'left'|'right'} [props.align='right']
 * @param {number}         [props.minWidth=320]
 * @param {string}         [props.ariaLabel='Menu']
 */
export function MenuDropdown({ trigger, items, align = 'right', minWidth = 320, ariaLabel = 'Menu' }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (item) => {
    if (item.disabled) return;
    setOpen(false);
    if (typeof item.onSelect === 'function') item.onSelect();
  };

  const TONE_BG = {
    action: 'bg-v2-action-soft text-v2-action',
    brand:  'bg-v2-brand-soft  text-v2-brand-strong',
    trust:  'bg-v2-trust-soft  text-v2-trust',
    cyan:   'bg-v2-cyan-soft   text-v2-cyan',
    success:'bg-v2-success-soft text-v2-success',
    warning:'bg-v2-warning-soft text-v2-warning',
    danger: 'bg-v2-danger-soft  text-v2-danger',
    muted:  'bg-v2-muted        text-v2-ink-muted',
  };

  // Inject the click handler into the trigger element.
  const enhancedTrigger = React.cloneElement(trigger, {
    onClick: (e) => {
      if (typeof trigger.props.onClick === 'function') trigger.props.onClick(e);
      if (e.defaultPrevented) return;
      setOpen((v) => !v);
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
  });

  return (
    <span ref={wrapperRef} className="relative inline-block">
      {enhancedTrigger}
      {open && (
        <div
          role="menu"
          aria-label={ariaLabel}
          className={
            /* P7J-RUN-001 — was z-30 + bg-v2-surface (CSS var). On the
             * Agentlets list the popover rendered translucent over the
             * table; underlying row text (Canvas / Delete buttons, trust
             * pills) bled through the menu items. Bump to z-50 and force
             * an opaque fallback background via style override below so
             * the menu always covers content beneath it, even if the
             * v2-bg-surface token resolves to a transparent value. */
            'absolute mt-1.5 z-50 ' +
            'bg-v2-surface border border-v2-border rounded-v2-md shadow-v2-lg ' +
            'py-1.5 slide-down ' +
            (align === 'right' ? 'right-0' : 'left-0')
          }
          style={{ minWidth, backgroundColor: 'var(--v2-bg-surface, #ffffff)' }}
        >
          {items.map((item, i) => {
            if (item.divider) {
              return <div key={item.id || `div-${i}`} role="separator" className="h-px bg-v2-border-subtle mx-2 my-1.5" />;
            }
            if (item.header) {
              return (
                <div
                  key={item.id || `hdr-${i}`}
                  className="text-[10px] uppercase tracking-wider font-semibold text-v2-ink-muted px-3 pt-2 pb-1.5"
                >
                  {item.header}
                </div>
              );
            }
            if (item.footer) {
              return (
                <div
                  key={item.id || `ftr-${i}`}
                  className="text-[10.5px] text-v2-ink-muted px-3 pt-2 mt-1 border-t border-v2-border-subtle flex items-center gap-1.5"
                >
                  {item.footer}
                </div>
              );
            }
            return (
              <button
                key={item.id}
                role="menuitem"
                type="button"
                disabled={item.disabled}
                onClick={() => handleSelect(item)}
                className={
                  'v2-focus-ring w-full text-left grid items-center gap-3 px-3 py-2 mx-1 rounded-v2-sm ' +
                  'transition-colors duration-v2 ' +
                  'disabled:opacity-50 disabled:cursor-not-allowed ' +
                  'enabled:hover:bg-v2-muted ' +
                  (item.icon ? 'grid-cols-[28px_1fr_14px]' : 'grid-cols-[1fr_14px]')
                }
                style={{ width: 'calc(100% - 0.5rem)' }}
              >
                {item.icon && (
                  <span
                    aria-hidden="true"
                    className={
                      'w-7 h-7 rounded-v2-sm flex items-center justify-center ' +
                      (TONE_BG[item.iconTone || 'action'])
                    }
                  >
                    {item.icon}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-v2-ink leading-tight">{item.label}</span>
                  {item.description && (
                    <span className="block text-[11.5px] text-v2-ink-muted leading-snug mt-0.5">
                      {item.description}
                    </span>
                  )}
                </span>
                <ChevronRight aria-hidden="true" size={14} className="text-v2-ink-muted" />
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

/**
 * MenuChevron — small visual helper for trigger buttons that want a
 * downward chevron next to the label. Use as `iconRight` on Button.
 */
export function MenuChevron({ open }) {
  return (
    <ChevronDown
      aria-hidden="true"
      className={'transition-transform duration-v2 ' + (open ? 'rotate-180' : '')}
    />
  );
}

export default MenuDropdown;
